import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories, mainSiteUrl } from '@/mocks/categories';

interface NavArticle {
  label: string;
  to: string;
  badge?: string;
}

const navArticles: NavArticle[] = [
  { label: 'Complete Guide', to: '/what-is-superbuy-spreadsheet' },
  { label: 'Shoes Guide', to: '/article/shoes-guide' },
  { label: 'Safety Guide', to: '/article/safety-guide' },
  { label: 'Beginner Guide', to: '/article/beginner-guide', badge: 'New' },
];

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatExpanded, setMobileCatExpanded] = useState(false);
  const [mobileGuideExpanded, setMobileGuideExpanded] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/60' : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="flex items-center justify-between h-16 md:h-18">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 group">
            <div className="w-8 h-8 md:w-9 md:h-9 flex items-center justify-center rounded-lg bg-primary-500 text-background-50">
              <i className="ri-file-list-3-line text-base md:text-lg"></i>
            </div>
            <span className="font-heading text-lg md:text-xl font-bold text-foreground-900 whitespace-nowrap">
              Superbuy <span className="text-primary-600">Spreadsheet</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-1">
            <Link to="/" className="px-4 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-950 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap">
              Home
            </Link>

            {/* Guides Dropdown */}
            <div className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-950 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                Guides
                <i className={`ri-arrow-down-s-line transition-transform duration-200 text-sm ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-56 bg-background-50 rounded-xl border border-background-200/80 py-2 animate-[fadeIn_0.15s_ease-out] shadow-lg shadow-foreground-900/5">
                  {navArticles.map((a) => (
                    <Link
                      key={a.to}
                      to={a.to}
                      className="flex items-center justify-between px-4 py-2.5 text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors cursor-pointer"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <span>{a.label}</span>
                      {a.badge && (
                        <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 border border-accent-200/50">{a.badge}</span>
                      )}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="px-4 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-950 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap">
              Blog
            </a>

            {/* Categories Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-foreground-700 hover:text-foreground-950 rounded-lg hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
              >
                Categories
                <i className={`ri-arrow-down-s-line transition-transform duration-200 text-sm ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full left-0 mt-2 w-64 bg-background-50 rounded-xl border border-background-200/80 py-2 animate-[fadeIn_0.15s_ease-out] shadow-lg shadow-foreground-900/5">
                  {categories.map((cat) => (
                    <a
                      key={cat.name}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-100 transition-colors cursor-pointer"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <div className="w-7 h-7 flex items-center justify-center rounded-md bg-primary-100 text-primary-600">
                        <i className={`${cat.icon} text-xs`}></i>
                      </div>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>
          </nav>

          {/* Desktop CTAs */}
          <div className="hidden md:flex items-center gap-2">
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 text-sm font-semibold text-primary-700 border border-primary-300 rounded-full hover:bg-primary-50 transition-colors whitespace-nowrap cursor-pointer"
            >
              Superbuy Sheet
            </a>
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 text-sm font-semibold text-background-50 bg-primary-500 rounded-full hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>

          {/* Mobile Hamburger */}
          <button
            className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <i className={`text-xl transition-transform duration-200 ${mobileMenuOpen ? 'ri-close-line rotate-90' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <>
          {/* Backdrop */}
          <div
            className="md:hidden fixed inset-0 top-16 bg-foreground-900/30 z-40 animate-[fadeIn_0.2s_ease-out]"
            onClick={() => setMobileMenuOpen(false)}
          />

          {/* Menu Panel */}
          <div className="md:hidden fixed top-16 left-0 right-0 bottom-0 bg-background-50 z-50 overflow-y-auto animate-[slideDown_0.25s_ease-out]">
            <div className="px-4 py-5 space-y-1">

              {/* Quick Nav */}
              <Link
                to="/"
                className="flex items-center gap-3 px-4 py-3 text-sm font-semibold text-foreground-800 rounded-xl hover:bg-background-100 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
              >
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-foreground-100 text-foreground-700">
                  <i className="ri-home-4-line"></i>
                </div>
                Home
              </Link>

              {/* Guides Accordion */}
              <div className="rounded-xl border border-background-200/60 overflow-hidden">
                <button
                  onClick={() => setMobileGuideExpanded(!mobileGuideExpanded)}
                  className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-foreground-800 hover:bg-background-50 transition-colors cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                      <i className="ri-book-open-line"></i>
                    </div>
                    SEO Guides
                  </div>
                  <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileGuideExpanded ? 'rotate-180' : ''}`}></i>
                </button>

                {mobileGuideExpanded && (
                  <div className="border-t border-background-200/60 py-2 px-4 bg-background-100/30 space-y-1">
                    {navArticles.map((a) => (
                      <Link
                        key={a.to}
                        to={a.to}
                        className="flex items-center justify-between px-3 py-2.5 text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-50 rounded-lg transition-colors"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        <span>{a.label}</span>
                        {a.badge && (
                          <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 border border-accent-200/50">{a.badge}</span>
                        )}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {/* Categories Accordion */}
              <div className="rounded-xl border border-background-200/60 overflow-hidden">
                <button
                  onClick={() => setMobileCatExpanded(!mobileCatExpanded)}
                  className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-foreground-800 hover:bg-background-50 transition-colors cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                      <i className="ri-grid-line"></i>
                    </div>
                    Product Categories
                    <span className="text-xs text-foreground-400 font-normal ml-1">({categories.length})</span>
                  </div>
                  <i className={`ri-arrow-down-s-line transition-transform duration-200 ${mobileCatExpanded ? 'rotate-180' : ''}`}></i>
                </button>

                {mobileCatExpanded && (
                  <div className="border-t border-background-200/60 py-2 px-3 bg-background-100/30">
                    <div className="grid grid-cols-2 gap-1">
                      {categories.map((cat) => (
                        <a
                          key={cat.name}
                          href={cat.url}
                          target="_blank"
                          rel="noopener nofollow"
                          className="flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm text-foreground-700 hover:text-foreground-950 hover:bg-background-50 transition-colors cursor-pointer"
                          onClick={() => setMobileMenuOpen(false)}
                        >
                          <div className="w-6 h-6 flex-shrink-0 flex items-center justify-center rounded-md bg-primary-100 text-primary-600">
                            <i className={`${cat.icon} text-xs`}></i>
                          </div>
                          <span className="truncate text-xs">{cat.name}</span>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-3 px-4 py-3 text-sm font-semibold text-foreground-800 rounded-xl hover:bg-background-100 transition-colors cursor-pointer"
              >
                <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                  <i className="ri-external-link-line"></i>
                </div>
                Visit Main Website
              </a>
            </div>

            {/* Sticky CTA Buttons */}
            <div className="sticky bottom-0 px-4 py-4 bg-background-50 border-t border-background-200/60 flex flex-col gap-2">
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="w-full py-3 text-sm font-semibold text-primary-700 border border-primary-300 rounded-full text-center hover:bg-primary-50 transition-colors whitespace-nowrap cursor-pointer"
              >
                Superbuy Sheet
              </a>
              <a
                href={mainSiteUrl}
                target="_blank"
                rel="noopener nofollow"
                className="w-full py-3 text-sm font-semibold text-background-50 bg-primary-500 rounded-full text-center hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                Buy It Now
              </a>
            </div>
          </div>
        </>
      )}
    </header>
  );
}